import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { getDocument, GlobalWorkerOptions, PDFDocumentProxy, RenderTask } from 'pdfjs-dist';
import { useTranslation } from 'react-i18next';
import LoadingWheel from '../shared/LoadingWheel';
import { authenticatedFetch, getApiBaseUrl } from '../../api/apiConfig';
import { boxToOverlay, FieldBox, isOcrFieldBox, OverlayRect } from './boundingBoxOverlay';
import { locateValueOverlay, PdfTextItem } from './locatePdfText';

const publicUrl = process.env.PUBLIC_URL || '';
GlobalWorkerOptions.workerSrc = `${publicUrl}/pdf.worker.min.js`;

export interface PreviewField {
  id?: string;
  pageNumber?: number | null;
  boundingBox?: FieldBox | null;
  label?: string;
  rawValue?: string | null;
}

interface DocumentPreviewProps {
  documentId: string;
  fileName?: string;
  fields: PreviewField[];
  selectedFieldId?: string | null;
  onSelectField?: (fieldId: string) => void;
  onDownload?: () => void;
}

const DocumentPreview: React.FC<DocumentPreviewProps> = ({
  documentId,
  fileName,
  fields,
  selectedFieldId,
  onSelectField,
  onDownload,
}) => {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const frameRef = useRef<HTMLDivElement | null>(null);
  const pdfDocRef = useRef<PDFDocumentProxy | null>(null);
  const renderTaskRef = useRef<RenderTask | null>(null);
  const objectUrlRef = useRef<string | null>(null);
  const fileBytesRef = useRef<ArrayBuffer | null>(null);
  const lastFrameWidthRef = useRef(0);

  const revokeObjectUrl = useCallback(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, []);

  const showPdfIframe = useCallback((bytes: ArrayBuffer) => {
    revokeObjectUrl();
    const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
    objectUrlRef.current = url;
    setObjectUrl(url);
    setKind('pdf-iframe');
    setPageCount(1);
    setError(false);
  }, [revokeObjectUrl]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [kind, setKind] = useState<'pdf' | 'pdf-iframe' | 'image' | 'other'>('other');
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [pageSize, setPageSize] = useState({ width: 1, height: 1 });
  const [pdfTextItems, setPdfTextItems] = useState<PdfTextItem[]>([]);
  const viewportRef = useRef<{ width: number; height: number; convertToViewportPoint: (x: number, y: number) => number[] } | null>(null);

  useEffect(() => {
    const targetPage = fields.find((field) => field.id === selectedFieldId)?.pageNumber;
    if (targetPage && targetPage >= 1) {
      setPageNumber(targetPage);
    }
  }, [fields, selectedFieldId]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(false);
      setPdfTextItems([]);
      viewportRef.current = null;
      fileBytesRef.current = null;
      revokeObjectUrl();
      setObjectUrl(null);
      pdfDocRef.current?.destroy();
      pdfDocRef.current = null;

      try {
        const file = await fetchDocumentBytes(documentId);
        if (cancelled) {
          return;
        }

        fileBytesRef.current = file.bytes;
        const resolvedKind = classifyPreview(file.mimeType, fileName);

        if (resolvedKind === 'pdf') {
          try {
            const pdf = await loadPdfDocument(file.bytes);
            if (cancelled) {
              pdf.destroy();
              return;
            }
            pdfDocRef.current = pdf;
            setKind('pdf');
            setPageCount(pdf.numPages);
            setPageNumber((current) => Math.min(Math.max(current, 1), pdf.numPages));
          } catch {
            if (cancelled) {
              return;
            }
            showPdfIframe(file.bytes);
          }
        } else if (resolvedKind === 'image') {
          const url = URL.createObjectURL(new Blob([file.bytes], { type: file.mimeType || 'application/octet-stream' }));
          objectUrlRef.current = url;
          setObjectUrl(url);
          setKind('image');
          setPageCount(1);
          setPageNumber(1);
        } else {
          setKind('other');
          setPageCount(1);
        }
      } catch {
        if (!cancelled) {
          setError(true);
          setKind('other');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
      renderTaskRef.current?.cancel();
      pdfDocRef.current?.destroy();
      pdfDocRef.current = null;
      revokeObjectUrl();
    };
  }, [documentId, fileName, revokeObjectUrl, showPdfIframe]);

  const renderPdfPage = useCallback(async () => {
    const pdf = pdfDocRef.current;
    const canvas = canvasRef.current;
    const frame = frameRef.current;
    if (!pdf || !canvas || kind !== 'pdf') {
      return;
    }

    renderTaskRef.current?.cancel();
    try {
      const page = await pdf.getPage(pageNumber);
      const base = page.getViewport({ scale: 1 });
      const availableWidth = frame?.clientWidth || 640;
      const scale = Math.max(0.5, availableWidth / base.width);
      const viewport = page.getViewport({ scale });
      const context = canvas.getContext('2d');
      if (!context || pdfDocRef.current !== pdf) {
        return;
      }

      canvas.width = viewport.width;
      canvas.height = viewport.height;
      setPageSize((current) =>
        current.width === base.width && current.height === base.height
          ? current
          : { width: base.width, height: base.height }
      );
      viewportRef.current = {
        width: viewport.width,
        height: viewport.height,
        convertToViewportPoint: (x, y) => viewport.convertToViewportPoint(x, y),
      };
      try {
        const textContent = await page.getTextContent();
        setPdfTextItems(asPdfTextItems(textContent.items));
      } catch {
        setPdfTextItems([]);
      }

      const task = page.render({ canvasContext: context, viewport });
      renderTaskRef.current = task;
      await task.promise;
    } catch (err) {
      if (isIgnorableRenderError(err) || pdfDocRef.current !== pdf) {
        return;
      }
      if (fileBytesRef.current) {
        showPdfIframe(fileBytesRef.current);
        return;
      }
      setError(true);
    }
  }, [kind, pageNumber, showPdfIframe]);

  useEffect(() => {
    if (kind !== 'pdf' || loading) {
      return undefined;
    }
    void renderPdfPage();
    return undefined;
  }, [kind, loading, pageNumber, renderPdfPage]);

  useEffect(() => {
    if (kind !== 'pdf' || !frameRef.current) {
      return undefined;
    }
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? 0;
      if (Math.abs(width - lastFrameWidthRef.current) < 2) {
        return;
      }
      lastFrameWidthRef.current = width;
      void renderPdfPage();
    });
    observer.observe(frameRef.current);
    return () => observer.disconnect();
  }, [kind, renderPdfPage]);

  const overlays = useMemo(() => {
    return fields
      .map((field) => {
        if (!field.id || (field.pageNumber ?? 1) !== pageNumber) {
          return null;
        }

        const rect = resolveFieldRect(field, kind, pageSize, pdfTextItems, viewportRef.current);
        return rect ? { field, rect } : null;
      })
      .filter((item): item is NonNullable<typeof item> => Boolean(item));
  }, [fields, kind, pageNumber, pageSize, pdfTextItems]);

  const selectedOverlay = overlays.find((item) => item.field.id === selectedFieldId) ?? null;
  const selectedOverlayRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const frame = frameRef.current;
    const overlay = selectedOverlayRef.current;
    if (frame && overlay) {
      const target = overlay.getBoundingClientRect();
      const viewport = frame.getBoundingClientRect();
      frame.scrollTo({ top: frame.scrollTop + target.top - viewport.top - frame.clientHeight / 2 + target.height / 2,
        behavior: 'smooth' });
    }
  }, [selectedFieldId, pageNumber, selectedOverlay?.rect.left, selectedOverlay?.rect.top]);

  if (loading) {
    return (
      <Stack sx={{ height: 480 }} justifyContent="center" alignItems="center">
        <LoadingWheel />
      </Stack>
    );
  }

  if (error || kind === 'other') {
    return (
      <Stack sx={{ p: 3, height: 480 }} justifyContent="center" alignItems="center" spacing={1}>
        <Typography color="text.secondary">{error ? t('reviewPage.previewFailed') : t('reviewPage.noPreview')}</Typography>
        {onDownload && (
          <Button onClick={onDownload} variant="secondary" size="md">
            {t('common.download')}
          </Button>
        )}
      </Stack>
    );
  }

  return (
    <Box>
      {kind === 'pdf' && pageCount > 1 && (
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ py: 1 }}>
          <IconButton
            size="small"
            onClick={() => setPageNumber((current) => Math.max(1, current - 1))}
            disabled={pageNumber <= 1}
            aria-label={t('reviewPage.previousPage')}
          >
            <ChevronLeftIcon />
          </IconButton>
          <Typography variant="textSM">
            {t('reviewPage.pageOf', { current: pageNumber, total: pageCount })}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setPageNumber((current) => Math.min(pageCount, current + 1))}
            disabled={pageNumber >= pageCount}
            aria-label={t('reviewPage.nextPage')}
          >
            <ChevronRightIcon />
          </IconButton>
        </Stack>
      )}
      <Box
        ref={frameRef}
        sx={{
          position: 'relative',
          overflow: 'auto',
          maxHeight: { xs: 480, lg: 'calc(100dvh - 220px)' },
          bgcolor: 'grey.100',
        }}
      >
        <Box sx={{ position: 'relative', display: 'block', width: '100%', lineHeight: 0 }}>
          {kind === 'pdf-iframe' && (
            <Box
              component="iframe"
              title={fileName ?? ''}
              src={objectUrl ?? undefined}
              sx={{ width: '100%', height: 640, border: 0, display: 'block' }}
            />
          )}
          {kind === 'pdf' ? (
            <canvas ref={canvasRef} style={{ display: 'block', width: '100%', height: 'auto' }} />
          ) : kind === 'image' ? (
            <Box
              component="img"
              src={objectUrl ?? undefined}
              alt={fileName}
              onLoad={(event) => {
                const image = event.currentTarget;
                setPageSize({ width: image.naturalWidth, height: image.naturalHeight });
              }}
              sx={{ width: '100%', display: 'block' }}
            />
          ) : null}
          {kind !== 'pdf-iframe' && selectedOverlay && (
            <Box
              ref={selectedOverlayRef}
              key={selectedOverlay.field.id}
              title={selectedOverlay.field.label}
              sx={{
                position: 'absolute',
                left: `${selectedOverlay.rect.left}%`,
                top: `${selectedOverlay.rect.top}%`,
                width: `${selectedOverlay.rect.width}%`,
                height: `${selectedOverlay.rect.height}%`,
                zIndex: 1,
                bgcolor: 'warning.200',
                border: '2px solid',
                borderColor: 'warning.600',
                borderRadius: '2px',
                opacity: 0.62,
                mixBlendMode: 'multiply',
                boxShadow: (theme) => `0 0 0 1px ${theme.palette.warning.main}`,
                pointerEvents: 'none',
              }}
              aria-label={t('reviewPage.extractedRegion')}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

async function fetchDocumentBytes(documentId: string): Promise<{ bytes: ArrayBuffer; mimeType: string }> {
  const response = await authenticatedFetch.fetch(`${getApiBaseUrl() ?? ''}/api/documents/${documentId}/file`);
  if (!response.ok) {
    throw new Error('Failed to load document');
  }

  return {
    bytes: await response.arrayBuffer(),
    mimeType: response.headers.get('content-type') ?? '',
  };
}

function classifyPreview(mimeType: string, fileName?: string): 'pdf' | 'image' | 'other' {
  const mime = mimeType.toLowerCase();
  const name = (fileName ?? '').toLowerCase();
  if (mime.includes('pdf') || name.endsWith('.pdf')) {
    return 'pdf';
  }
  if (mime.startsWith('image/') || /\.(png|jpe?g|gif|webp|tif{1,2}|bmp)$/i.test(name)) {
    return 'image';
  }
  return 'other';
}

async function loadPdfDocument(data: ArrayBuffer): Promise<PDFDocumentProxy> {
  return getDocument({
    data: new Uint8Array(data.slice(0)),
    isEvalSupported: false,
    useSystemFonts: false,
    cMapUrl: `${publicUrl}/cmaps/`,
    cMapPacked: true,
    standardFontDataUrl: `${publicUrl}/standard_fonts/`,
  }).promise;
}

function isIgnorableRenderError(err: unknown): boolean {
  const failure = err as { name?: string; message?: string };
  const name = failure.name ?? '';
  const message = (failure.message ?? '').toLowerCase();
  return name === 'RenderingCancelledException'
    || name === 'AbortException'
    || message.includes('cancel')
    || message.includes('destroy');
}

function resolveFieldRect(
  field: PreviewField,
  kind: 'pdf' | 'pdf-iframe' | 'image' | 'other',
  pageSize: { width: number; height: number },
  pdfTextItems: PdfTextItem[],
  viewport: { width: number; height: number; convertToViewportPoint: (x: number, y: number) => number[] } | null,
): OverlayRect | null {
  if (isOcrFieldBox(field.boundingBox)) {
    const ocrRect = boxToOverlay(field.boundingBox, pageSize.width, pageSize.height);
    if (ocrRect) {
      return ocrRect;
    }
  }

  if (kind === 'pdf' && field.rawValue && pdfTextItems.length > 0 && viewport) {
    const textRect = locateValueOverlay(pdfTextItems, field.rawValue, viewport);
    if (textRect) {
      return textRect;
    }
  }

  if (field.boundingBox) {
    return boxToOverlay(field.boundingBox, pageSize.width, pageSize.height);
  }

  return null;
}

function asPdfTextItems(items: unknown[]): PdfTextItem[] {
  return items.filter((item): item is PdfTextItem => {
    if (typeof item !== 'object' || item === null) {
      return false;
    }
    const candidate = item as Partial<PdfTextItem>;
    return typeof candidate.str === 'string'
      && Array.isArray(candidate.transform)
      && typeof candidate.width === 'number'
      && typeof candidate.height === 'number';
  });
}

export default DocumentPreview;
